/**
 * Gestione foto intervento.
 *  - comprimiFoto: ridimensiona e comprime lato client (da 5 MB a ~200 KB)
 *  - coda IndexedDB: le foto scattate offline restano sul dispositivo
 *    finché non torna la rete. localStorage non basta: ha ~5 MB totali
 *    e li condivide con le firme in attesa.
 */
import api from './api';

const LATO_MAX = 1280;
const QUALITA  = 0.7;
const DB_NOME  = 'quindi_foto';
const STORE    = 'coda';

export function comprimiFoto(file) {
  return new Promise((risolvi, rifiuta) => {
    const reader = new FileReader();
    reader.onerror = () => rifiuta(new Error('Lettura file fallita'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => rifiuta(new Error('Immagine non valida'));
      img.onload = () => {
        let { width: w, height: h } = img;
        if (w > LATO_MAX || h > LATO_MAX) {
          const scala = LATO_MAX / Math.max(w, h);
          w = Math.round(w * scala);
          h = Math.round(h * scala);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);          // evita sfondi neri sui PNG trasparenti
        ctx.drawImage(img, 0, 0, w, h);
        risolvi(canvas.toDataURL('image/jpeg', QUALITA));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function apriDb() {
  return new Promise((risolvi, rifiuta) => {
    const req = indexedDB.open(DB_NOME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE))
        db.createObjectStore(STORE, { keyPath: 'clientId' });
    };
    req.onsuccess = () => risolvi(req.result);
    req.onerror = () => rifiuta(req.error);
  });
}

export async function accodaFoto(interventoId, base64, didascalia) {
  const db = await apriDb();
  const clientId = 'f' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  return new Promise((risolvi, rifiuta) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ clientId, interventoId, base64, didascalia, creata: Date.now() });
    tx.oncomplete = () => risolvi(clientId);
    tx.onerror = () => rifiuta(tx.error);
  });
}

export async function fotoInCoda(interventoId = null) {
  const db = await apriDb();
  return new Promise((risolvi) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => {
      const tutte = req.result || [];
      risolvi(interventoId ? tutte.filter(f => f.interventoId === interventoId) : tutte);
    };
    req.onerror = () => risolvi([]);
  });
}

async function rimuoviDaCoda(clientId) {
  const db = await apriDb();
  return new Promise((risolvi) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(clientId);
    tx.oncomplete = () => risolvi();
    tx.onerror = () => risolvi();
  });
}

/** Invia le foto in coda. Ritorna { inviate, rimaste, scartate }. */
export async function sincronizzaFoto(interventoId = null) {
  if (!navigator.onLine) return { inviate: 0, rimaste: -1, scartate: 0 };
  const coda = await fotoInCoda(interventoId);
  let inviate = 0, scartate = 0;

  for (const f of coda) {
    try {
      await api.post(`/intervento/${f.interventoId}/foto`, {
        base64: f.base64, didascalia: f.didascalia, clientId: f.clientId,
      });
      await rimuoviDaCoda(f.clientId);
      inviate++;
    } catch (e) {
      const st = e.response?.status;
      if (st >= 400 && st < 500) {
        // rifiutata dal server (limite raggiunto, formato): inutile ritentare
        await rimuoviDaCoda(f.clientId);
        scartate++;
      }
    }
  }
  const rimaste = (await fotoInCoda(interventoId)).length;
  return { inviate, rimaste, scartate };
}
